import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  BarChart3, Bell, Bot, Building2, CalendarDays, CheckSquare, ChevronDown, Contact, HandCoins, Home, Import, LifeBuoy, LogOut,
  Menu, MessagesSquare, Search, Settings, Shield, Sparkles, Users, Workflow, X, Zap,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { LanguageSwitcher, Logo } from '../components/brand';
import { Avatar, Button, Modal, Spinner } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, relativeTime, setTimeZone } from '../lib/format';
import { useOrg } from '../lib/org';
import type { Permission } from '@high-crm/shared';

interface NavItem { to: string; key: string; icon: ReactNode; permission?: Permission; feature?: string; end?: boolean }

const NAV_GROUPS: Array<{ key: string; items: NavItem[] }> = [
  {
    key: 'nav.groups.sales',
    items: [
      { to: '/app', key: 'nav.dashboard', icon: <Home className="size-4" />, end: true },
      { to: '/app/leads', key: 'nav.leads', icon: <Zap className="size-4" />, permission: 'leads.view' },
      { to: '/app/deals', key: 'nav.deals', icon: <HandCoins className="size-4" />, permission: 'deals.view' },
      { to: '/app/contacts', key: 'nav.contacts', icon: <Contact className="size-4" />, permission: 'contacts.view' },
      { to: '/app/companies', key: 'nav.companies', icon: <Building2 className="size-4" />, permission: 'companies.view' },
      { to: '/app/tasks', key: 'nav.tasks', icon: <CheckSquare className="size-4" />, permission: 'tasks.view' },
      { to: '/app/calendar', key: 'nav.calendar', icon: <CalendarDays className="size-4" />, permission: 'tasks.view' },
      { to: '/app/inbox', key: 'nav.inbox', icon: <MessagesSquare className="size-4" />, permission: 'inbox.view' },
    ],
  },
  {
    key: 'nav.groups.insights',
    items: [
      { to: '/app/analytics', key: 'nav.analytics', icon: <BarChart3 className="size-4" />, permission: 'reports.view' },
      { to: '/app/ai', key: 'nav.ai', icon: <Sparkles className="size-4" /> },
    ],
  },
  {
    key: 'nav.groups.workspace',
    items: [
      { to: '/app/automations', key: 'nav.automations', icon: <Workflow className="size-4" />, permission: 'automations.manage' },
      { to: '/app/imports', key: 'nav.imports', icon: <Import className="size-4" />, permission: 'leads.import' },
      { to: '/app/team', key: 'nav.team', icon: <Users className="size-4" /> },
      { to: '/app/settings', key: 'nav.settings', icon: <Settings className="size-4" /> },
      { to: '/app/support', key: 'nav.support', icon: <LifeBuoy className="size-4" /> },
    ],
  },
];

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { ctx, can } = useOrg();
  return (
    <div className="flex h-full flex-col bg-sidebar text-slate-300">
      <div className="flex h-16 items-center px-5">
        <Logo to="/app" light name={ctx?.branding.display_name ?? undefined} />
      </div>
      <nav className="scroll-thin flex-1 space-y-6 overflow-y-auto px-3 py-4">
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((item) => !item.permission || can(item.permission));
          if (!items.length) return null;
          return (
            <div key={group.key}>
              <p className="px-3 pb-2 text-[11px] font-semibold tracking-wider text-slate-500 uppercase">{t(group.key)}</p>
              <div className="space-y-0.5">
                {items.map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate}
                    className={({ isActive }) => clsx('flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive ? 'bg-white/10 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white')}>
                    {item.icon}
                    {t(item.key)}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      {ctx?.branding.show_powered_by !== false && (
        <div className="border-t border-white/10 px-5 py-3 text-xs text-slate-500">{t('nav.poweredBy')}</div>
      )}
    </div>
  );
}

function GlobalSearch() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const search = useQuery({
    queryKey: ['search', q],
    queryFn: () => api.get<Record<string, Array<{ id: string; title: string; number?: number; phone?: string; email?: string }>>>('/api/search', { q }),
    enabled: q.trim().length >= 2,
  });
  const go = (path: string) => { setOpen(false); setQ(''); navigate(path); };
  const groups: Array<[string, string]> = [['leads', 'leads'], ['contacts', 'contacts'], ['companies', 'companies'], ['deals', 'deals'], ['tasks', 'tasks']];
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="flex h-9 w-full max-w-md items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-500 hover:border-slate-300">
        <Search className="size-4" />
        <span className="flex-1 truncate text-left">{t('search.placeholder')}</span>
        <kbd className="hidden rounded border border-slate-200 bg-white px-1.5 text-[10px] font-medium text-slate-500 sm:inline">Ctrl K</kbd>
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title={t('search.title')} size="lg">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} className="input mb-4" placeholder={t('search.placeholder')} />
        {search.isFetching && <Spinner />}
        {search.data && (
          <div className="space-y-4">
            {groups.map(([key, path]) => {
              const items = search.data[key] ?? [];
              if (!items.length) return null;
              return (
                <div key={key}>
                  <p className="mb-1 text-xs font-semibold tracking-wide text-slate-500 uppercase">{t(`nav.${key}`)}</p>
                  {items.map((item) => (
                    <button key={item.id} type="button" onClick={() => go(key === 'tasks' ? '/app/tasks' : `/app/${path}/${item.id}`)}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50">
                      <span className="truncate font-medium text-slate-800">{item.number ? `#${item.number} · ` : ''}{item.title}</span>
                      <span className="ml-3 shrink-0 text-xs text-slate-500">{item.phone ?? item.email ?? ''}</span>
                    </button>
                  ))}
                </div>
              );
            })}
            {groups.every(([key]) => !(search.data[key] ?? []).length) && <p className="text-sm text-slate-500">{t('search.noResults')}</p>}
          </div>
        )}
      </Modal>
    </>
  );
}

interface NotificationItem { id: string; type: string; title: string; body: string | null; entity_type: string | null; entity_id: string | null; read_at: string | null; created_at: string }

function NotificationsBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { orgId } = useOrg();
  const { data } = useQuery({
    queryKey: ['notifications', orgId],
    queryFn: () => api.get<{ data: NotificationItem[]; unread: number }>('/api/notifications', { limit: 15 }),
    refetchInterval: 60_000,
    enabled: !!orgId,
  });
  useEffect(() => {
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const openItem = async (n: NotificationItem) => {
    if (!n.read_at) await api.post(`/api/notifications/${n.id}/read`);
    void qc.invalidateQueries({ queryKey: ['notifications'] });
    setOpen(false);
    if (n.entity_type && n.entity_id && ['lead', 'deal', 'contact', 'company'].includes(n.entity_type)) {
      navigate(`/app/${n.entity_type === 'company' ? 'companies' : `${n.entity_type}s`}/${n.entity_id}`);
    } else if (n.entity_type === 'task') navigate('/app/tasks');
    else if (n.entity_type?.startsWith('ai')) navigate('/app/ai');
    else if (n.entity_type === 'import') navigate('/app/imports');
  };
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-label={t('notifications.title')}
        className="relative flex size-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900">
        <Bell className="size-5" />
        {!!data?.unread && <span className="absolute top-1 right-1 flex min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{data.unread > 9 ? '9+' : data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-semibold">{t('notifications.title')}</p>
            <button type="button" className="text-xs font-medium text-brand-700 hover:underline"
              onClick={async () => { await api.post('/api/notifications/read-all'); void qc.invalidateQueries({ queryKey: ['notifications'] }); }}>
              {t('notifications.markAll')}
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {!data?.data.length && <p className="px-4 py-8 text-center text-sm text-slate-500">{t('notifications.empty')}</p>}
            {data?.data.map((n) => (
              <button key={n.id} type="button" onClick={() => void openItem(n)}
                className={clsx('flex w-full gap-3 border-b border-slate-50 px-4 py-3 text-left hover:bg-slate-50', !n.read_at && 'bg-brand-50/40')}>
                <span className={clsx('mt-1.5 size-2 shrink-0 rounded-full', n.read_at ? 'bg-transparent' : 'bg-brand')} />
                <span className="min-w-0">
                  <span className="block text-sm text-slate-800">{n.title}</span>
                  {n.body && <span className="block truncate text-xs text-slate-500">{n.body}</span>}
                  <span className="text-xs text-slate-400">{relativeTime(n.created_at)}</span>
                </span>
              </button>
            ))}
          </div>
          <Link to="/app/notifications" onClick={() => setOpen(false)} className="block px-4 py-2.5 text-center text-sm font-medium text-brand-700 hover:bg-slate-50">
            {t('notifications.viewAll')}
          </Link>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const { me, ctx, switchOrg } = useOrg();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const name = me?.profile?.full_name ?? me?.profile?.email ?? '';
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-100">
        <Avatar name={name} />
        <span className="hidden max-w-36 text-left md:block">
          <span className="block truncate text-sm font-medium text-slate-800">{name}</span>
          <span className="block truncate text-xs text-slate-500">{ctx?.organization.name}</span>
        </span>
        <ChevronDown className="hidden size-4 text-slate-400 md:block" />
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
          <div className="px-3 py-2">
            <p className="truncate text-sm font-medium">{name}</p>
            <p className="truncate text-xs text-slate-500">{me?.profile?.email} · {ctx?.membership.role_name}</p>
          </div>
          {me && me.organizations.length > 1 && (
            <div className="border-t border-slate-100 py-1">
              <p className="px-3 py-1 text-[11px] font-semibold tracking-wider text-slate-400 uppercase">{t('nav.workspaces')}</p>
              {me.organizations.map((o) => (
                <button key={o.id} type="button" onClick={() => { switchOrg(o.id); setOpen(false); navigate('/app'); }}
                  className={clsx('w-full truncate rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50', o.id === ctx?.organization.id && 'font-semibold text-brand-700')}>
                  {o.name}
                </button>
              ))}
            </div>
          )}
          <div className="border-t border-slate-100 py-1">
            <Link to="/onboarding?new=1" onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">{t('nav.newWorkspace')}</Link>
            {me?.platform_admin_role && (
              <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <Shield className="size-4" /> {t('nav.superAdmin')}
              </Link>
            )}
            <button type="button" onClick={async () => { await signOut(); navigate('/login'); }}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">
              <LogOut className="size-4" /> {t('nav.signOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SubscriptionBanner() {
  const { t } = useTranslation();
  const { ctx, can } = useOrg();
  if (!ctx) return null;
  const sub = ctx.subscription;
  if (!ctx.writable) {
    return (
      <div className="flex flex-wrap items-center justify-center gap-3 bg-amber-50 px-4 py-2 text-sm text-amber-900">
        {ctx.organization.status === 'pending_deletion' ? t('banner.pendingDeletion', { date: formatDate(ctx.organization.deletion_scheduled_for) }) : t('banner.readOnly')}
        {can('billing.manage') && <Link to="/app/settings/billing"><Button size="sm">{t('banner.upgrade')}</Button></Link>}
      </div>
    );
  }
  if (sub?.status === 'trialing' && sub.trial_ends_at) {
    const days = Math.max(0, Math.ceil((new Date(sub.trial_ends_at).getTime() - Date.now()) / 86_400_000));
    return (
      <div className="flex flex-wrap items-center justify-center gap-3 bg-brand-50 px-4 py-1.5 text-sm text-brand-700">
        <Bot className="size-4" />
        {t('banner.trial', { count: days })}
        {can('billing.manage') && <Link to="/app/settings/billing" className="font-semibold underline">{t('banner.choosePlan')}</Link>}
      </div>
    );
  }
  return null;
}

export function AppLayout() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { ctx } = useOrg();
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => { if (ctx?.settings.timezone) setTimeZone(ctx.settings.timezone); }, [ctx?.settings.timezone]);

  return (
    <div className="flex h-screen overflow-hidden">
      <aside className="hidden w-64 shrink-0 lg:block"><Sidebar /></aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative h-full w-72"><Sidebar onNavigate={() => setMobileOpen(false)} /></aside>
          <button type="button" className="absolute top-4 left-[19rem] rounded-full bg-white p-2" onClick={() => setMobileOpen(false)} aria-label={t('common.close')}>
            <X className="size-4" />
          </button>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <SubscriptionBanner />
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 sm:px-6">
          <button type="button" className="rounded-lg p-2 text-slate-600 lg:hidden" onClick={() => setMobileOpen(true)} aria-label={t('common.menu')}>
            <Menu className="size-5" />
          </button>
          <div className="min-w-0 flex-1"><GlobalSearch /></div>
          <LanguageSwitcher compact />
          <NotificationsBell />
          <UserMenu />
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
